import React, { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import AnnotationBody, { AnnotationAvatar } from './AnnotationBody';
import AnnotationContextBadge from './AnnotationContextBadge';
import AnnotationComposer from './AnnotationComposer';
import AnnotationThread from './AnnotationThread';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { formatRelativeTime, formatDate } from 'features/agent-analytics/components/runCells';

/**
 * One comment and its thread — with no box of its own.
 *
 * This used to draw a bordered card per comment, inside a bordered section,
 * inside the bordered tab panel: three frames around one sentence, and a
 * fourth around the reply box nested in the card. Everywhere comments appear
 * they are already inside a container, so the comment brings none. What
 * separates one comment from the next is space and a hairline, which is all
 * that was ever needed.
 *
 * The only bordered thing left is the composer, because a box you type into
 * should look like one.
 */
export default function AnnotationCard({
  annotation,
  currentUser,
  onReply,
  onEdit,
  onDelete,
  onOpenContext,
  /** Show which run or span this is on — true wherever a list spans several. */
  showContext = false,
}) {
  const { confirm: confirmDialog } = useAppDialog();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const isOwn = currentUser?.id != null && Number(annotation.author?.id) === Number(currentUser.id);

  return (
    <article className={annotation.pending ? 'opacity-60' : ''}>
      <div className="flex items-start gap-2.5">
        <AnnotationAvatar name={annotation.author?.name} src={annotation.author?.avatarUrl} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[13px] font-medium text-[color:var(--text-primary)]">
              {annotation.author?.name}
            </span>
            <span
              className="flex-none text-[12px] text-[color:var(--text-muted)]"
              title={formatDate(annotation.createdAt)}
            >
              {annotation.pending ? 'Sending…' : formatRelativeTime(annotation.createdAt)}
            </span>
            {annotation.edited ? (
              <span className="flex-none text-[12px] text-[color:var(--text-muted)]">Edited</span>
            ) : null}
            <div className="ml-auto flex flex-none items-center gap-1">
              {showContext ? (
                <AnnotationContextBadge
                  context={annotation.context}
                  onOpenContext={onOpenContext}
                  withLabel
                />
              ) : null}
              {isOwn && !annotation.pending ? (
                <div className="relative">
                  <button
                    type="button"
                    aria-label="Annotation actions"
                    onClick={() => setMenuOpen((v) => !v)}
                    className="flex h-5 w-5 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                  >
                    <MoreHorizontal size={14} />
                  </button>
                  {menuOpen ? (
                    <>
                      <button
                        type="button"
                        className="fixed inset-0 z-10 cursor-default"
                        aria-label="Close menu"
                        onClick={() => setMenuOpen(false)}
                      />
                      <div className="absolute right-0 z-20 mt-1 w-32 overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg">
                        <button
                          type="button"
                          className="block w-full px-3 py-1.5 text-left text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover"
                          onClick={() => { setMenuOpen(false); setEditing(true); }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="block w-full px-3 py-1.5 text-left text-[12px] text-[color:var(--feedback-error)] hover:bg-surface-hover"
                          onClick={async () => {
                            setMenuOpen(false);
                            // The app's own dialog, not the browser's: centred,
                            // themed, and the same shell every other confirm uses.
                            const ok = await confirmDialog('This annotation and its replies will be removed.', {
                              title: 'Delete annotation',
                              confirmLabel: 'Delete',
                              danger: true,
                            });
                            if (ok) onDelete?.(annotation.id);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          {editing ? (
            <div className="mt-1.5">
              <AnnotationComposer
                context={annotation.context}
                conversationId={annotation.conversationId}
                initialBody={annotation.body}
                initialMentions={annotation.mentions || []}
                placeholder="Edit your annotation..."
                submitLabel="Save"
                onCancel={() => setEditing(false)}
                onSubmit={(payload) => {
                  setEditing(false);
                  onEdit?.(annotation.id, payload);
                }}
              />
            </div>
          ) : (
            <AnnotationBody body={annotation.body} mentions={annotation.mentions} className="mt-0.5" />
          )}

          <AnnotationThread
            annotation={annotation}
            currentUser={currentUser}
            onReply={onReply}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      </div>
    </article>
  );
}
